import { useCallback, useEffect, useRef, useState } from 'react';
import { type FoundReport, formatShortCode, mapsUrl, normalizeTagId, qrFinderUrl } from '@notlost/shared';
import { Logo, PinIcon, ShieldCheck } from '../components/Brand';
import { Footer, Header } from '../components/Layout';
import { QrCode } from '../components/QrCode';
import { ApiError, getOwnerReports, markReturned } from '../lib/api';
import { t } from '../i18n';

const POLL_MS = 4000;
type AlertState = 'off' | 'on' | 'blocked' | 'unsupported';

/**
 * Owner ping page, opened from the secret owner link (/o/{tagId}#k={key}).
 * The key lives in the URL fragment, so it never reaches CloudFront logs.
 * Polls while open and pings with a notification, vibration and sound.
 * Email pings, when turned on, are sent by the API.
 */
export function Owner({ rawId }: { rawId: string }) {
  const tagId = normalizeTagId(rawId);
  const [ownerKey] = useState(() => readOwnerKey(tagId));
  const [label, setLabel] = useState('');
  const [notifyEmail, setNotifyEmail] = useState<string | undefined>();
  const [demoExpiresAt, setDemoExpiresAt] = useState<string | undefined>();
  const [reports, setReports] = useState<FoundReport[] | null>(null);
  const [error, setError] = useState<'forbidden' | 'loadFailed' | null>(null);
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const [alerts, setAlerts] = useState<AlertState>(initialAlertState);
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());
  const seen = useRef<Set<string> | null>(null);
  const audio = useRef<AudioContext | null>(null);

  const ping = useCallback(
    (report: FoundReport, itemLabel: string) => {
      navigator.vibrate?.([120, 60, 120]);
      beep(audio.current);
      if (alerts !== 'on') return;
      const body = report.kind === 'location' ? t('owner.notify.location') : t(`owner.kind.${report.kind}`);
      const url = report.lat !== undefined && report.lng !== undefined ? mapsUrl(report.lat, report.lng) : undefined;
      void showNotification(t('owner.notify.title', { label: itemLabel }), {
        body,
        tag: report.id,
        icon: '/brand/icon-192.png',
        badge: '/brand/icon-192.png',
        data: { url },
      });
    },
    [alerts],
  );

  const load = useCallback(async () => {
    if (!tagId || !ownerKey) return;
    try {
      const res = await getOwnerReports(tagId, ownerKey);
      setLabel(res.label);
      setNotifyEmail(res.notifyEmail);
      setDemoExpiresAt(res.demoExpiresAt);
      setReports(res.reports);
      setError(null);
      if (seen.current === null) {
        // First load: everything already there is history, not a new ping.
        seen.current = new Set(res.reports.map((r) => r.id));
        return;
      }
      const fresh = res.reports.filter((r) => !seen.current!.has(r.id));
      if (fresh.length > 0) {
        for (const r of fresh) seen.current.add(r.id);
        setFreshIds((prev) => new Set([...prev, ...fresh.map((r) => r.id)]));
        ping(fresh[0]!, res.label);
      }
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'loadFailed');
    }
  }, [tagId, ownerKey, ping]);

  useEffect(() => {
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!visible || error === 'forbidden') return;
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [visible, load, error]);

  async function enableAlerts() {
    // A user gesture unlocks audio on iOS and Android.
    audio.current ??= new AudioContext();
    void audio.current.resume();
    if (!('Notification' in window)) return setAlerts('unsupported');
    const result = await Notification.requestPermission();
    setAlerts(result === 'granted' ? 'on' : result === 'denied' ? 'blocked' : 'off');
  }

  async function confirmReturned(reportId: string) {
    if (!tagId || !ownerKey) return;
    try {
      const res = await markReturned(tagId, ownerKey, reportId);
      const at = res.returnedAt ?? new Date().toISOString();
      setReports((prev) => prev?.map((r) => (r.id === reportId ? { ...r, returnedAt: r.returnedAt ?? at } : r)) ?? prev);
    } catch {
      setError('loadFailed');
    }
  }

  if (!tagId || !ownerKey || error === 'forbidden') {
    return (
      <Shell>
        <div className="card mx-auto mt-6 max-w-lg p-6 text-body">{!ownerKey ? t('owner.noKey') : t('owner.forbidden')}</div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:items-start lg:gap-10">
        <div className="flex flex-col gap-5 lg:sticky lg:top-24">
          <section className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-50">
              <Logo size={34} />
            </div>
            <div className="min-w-0">
              <p className="eyebrow">{t('owner.title')}</p>
              <h1 className="break-words text-2xl font-semibold tracking-tight leading-tight sm:text-3xl">{label || '...'}</h1>
            </div>
          </section>
          <ScanCard tagId={tagId} />
          {demoExpiresAt && (
            <p className="text-sm text-muted">
              {t('owner.demoExpires', {
                date: new Date(demoExpiresAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
              })}{' '}
              {t('owner.keepLink')}
            </p>
          )}
        </div>

        <div>
          <section className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-surface px-3 py-1.5 text-sm font-semibold text-body">
                <span className={`h-2.5 w-2.5 rounded-full ${visible ? 'animate-pulse bg-success' : 'bg-muted'}`} />
                {visible ? t('owner.live') : t('owner.paused')}
              </span>
              {notifyEmail && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
                  <MailIcon />
                  {t('owner.email', { email: notifyEmail })}
                </span>
              )}
            </div>
            {alerts === 'blocked' ? null : alerts === 'on' ? (
              <span className="rounded-full bg-success-50 px-3 py-1.5 text-sm font-semibold text-success">{t('owner.alerts.on')}</span>
            ) : (
              <button type="button" onClick={() => void enableAlerts()} className="btn btn-primary min-h-11 text-sm">
                {t('owner.alerts.enable')}
              </button>
            )}
          </section>
          <p className="mt-3 text-sm text-muted">
            {alerts === 'blocked' ? t('owner.alerts.blocked') : alerts === 'unsupported' ? t('owner.alerts.unsupported') : t('owner.alerts.keepOpen')}
          </p>
          {error === 'loadFailed' && <p className="mt-2 text-sm font-semibold text-danger">{t('owner.loadFailed')}</p>}

          <section className="mt-5 flex flex-col gap-4" aria-live="polite">
            {reports !== null && reports.length === 0 && (
              <p className="rounded-2xl border-2 border-dashed border-line bg-white p-6 text-body">{t('owner.empty')}</p>
            )}
            {reports?.map((r) => (
              <ReportCard key={r.id} report={r} fresh={freshIds.has(r.id)} onReturned={() => void confirmReturned(r.id)} />
            ))}
          </section>
        </div>
      </div>
    </Shell>
  );
}

function ReportCard({ report, fresh, onReturned }: { report: FoundReport; fresh: boolean; onReturned: () => void }) {
  const hasSpot = report.lat !== undefined && report.lng !== undefined;
  return (
    <article className={`card p-5 ${fresh ? 'ring-2 ring-blue' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue">
            <PinIcon className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-lg font-bold">{t(`owner.kind.${report.kind}`)}</h2>
            <p className="text-sm text-muted">
              {relativeTime(report.createdAt)} · {new Date(report.createdAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>
        </div>
        {fresh && <span className="shrink-0 rounded-full bg-blue px-3 py-1 text-xs font-bold text-white">{t('owner.new')}</span>}
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        {report.accuracy !== undefined && (
          <span className="rounded-full bg-surface px-3 py-1 text-body">{t('owner.accuracy', { meters: report.accuracy })}</span>
        )}
        {report.helperLevel && (
          <span className="rounded-full bg-amber-50 px-3 py-1 font-medium text-ink">{t('owner.helper', { level: report.helperLevel })}</span>
        )}
      </div>
      {report.message && <p className="mt-3 rounded-xl bg-surface p-3 text-body">{t('owner.note', { message: report.message })}</p>}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        {hasSpot && (
          <a href={mapsUrl(report.lat!, report.lng!)} target="_blank" rel="noopener noreferrer" className="btn btn-primary flex-1">
            {t('owner.openMaps')}
          </a>
        )}
        {report.returnedAt ? (
          <span className="btn flex-1 bg-success-50 text-success">
            <ShieldCheck className="h-5 w-5" />
            {t('owner.returnedDone')}
          </span>
        ) : (
          <button type="button" onClick={onReturned} className="btn btn-secondary flex-1" title={t('owner.returnedHint')}>
            {t('owner.returned')}
          </button>
        )}
      </div>
    </article>
  );
}

/** The tag's finder QR on screen, so someone nearby can scan it to test. */
function ScanCard({ tagId }: { tagId: string }) {
  const [copied, setCopied] = useState(false);
  const qrValue = qrFinderUrl(window.location.origin, tagId);
  const finderLink = `${window.location.origin}/t/${tagId}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(finderLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked. The code is still visible on the card.
    }
  }

  return (
    <section className="card p-5">
      <h2 className="text-lg font-bold">{t('owner.qr.title')}</h2>
      <p className="mt-1 text-sm text-body">{t('owner.qr.body')}</p>
      <div className="mx-auto mt-4 max-w-64 rounded-2xl border border-line p-2 lg:max-w-none">
        <QrCode value={qrValue} label={t('owner.qr.alt')} />
      </div>
      <p className="mt-3 text-center text-[19px] font-semibold tracking-[0.06em] tabular-nums">{t('owner.qr.code', { code: formatShortCode(tagId) })}</p>
      <button type="button" onClick={() => void copy()} className="mt-1 min-h-11 w-full text-center text-sm font-semibold text-blue">
        {copied ? t('owner.qr.copied') : t('owner.qr.copy')}
      </button>
    </section>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      <Header />
      <main className="mx-auto w-full max-w-[1024px] flex-1 px-5 pb-16 pt-8">{children}</main>
      <Footer />
    </div>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="m4 7 8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

/** Reads #k= from the link, remembers it for this tag, then strips it from the address bar. */
function readOwnerKey(tagId: string | null): string | null {
  if (!tagId) return null;
  const storageKey = `notlost.owner.${tagId}`;
  const fromHash = new URLSearchParams(window.location.hash.slice(1)).get('k');
  try {
    if (fromHash) {
      localStorage.setItem(storageKey, fromHash);
      window.history.replaceState(null, '', `/o/${tagId}`);
      return fromHash;
    }
    return localStorage.getItem(storageKey);
  } catch {
    return fromHash;
  }
}

/** Android Chrome only allows notifications via the service worker, so try that first. */
async function showNotification(title: string, options: NotificationOptions): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) return await reg.showNotification(title, options);
    new Notification(title, options);
  } catch {
    // Vibration and sound already fired. A missing notification is not fatal.
  }
}

function initialAlertState(): AlertState {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission === 'granted' ? 'on' : Notification.permission === 'denied' ? 'blocked' : 'off';
}

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return t('time.justNow');
  if (mins < 60) return t('time.minutes', { n: mins });
  if (mins < 1440) return t('time.hours', { n: Math.floor(mins / 60) });
  return t('time.days', { n: Math.floor(mins / 1440) });
}

function beep(ctx: AudioContext | null): void {
  if (!ctx || ctx.state !== 'running') return;
  const now = ctx.currentTime;
  for (const [offset, freq] of [
    [0, 880],
    [0.18, 1320],
  ] as const) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, now + offset);
    gain.gain.exponentialRampToValueAtTime(0.25, now + offset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.16);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + offset);
    osc.stop(now + offset + 0.18);
  }
}
